import React, { useMemo } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { Breadcrumb } from 'react-bootstrap';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';

import type { IBreadcrumbPathItem } from '@/store/types';

import { getBreadcrumbPath } from '@/helpers';
import { useAppSelector, useResourceNavigation } from '@/hooks';
import { colors, otherColors } from '@/styles/colors';
import { selectFoldersList } from '@/store/selectors/resourcesSelectors';
import { getPathFromCrumb } from '@/helpers/resources/breadcrumb';

const resourcesBreadcrumbSelector = createSelector(selectFoldersList, foldersList => ({
  foldersList,
}));

const ResourcesBreadcrumb = (): React.ReactElement => {
  const { t } = useTranslation();
  const { selectedFolder } = useResourceNavigation();
  const { foldersList } = useAppSelector(resourcesBreadcrumbSelector);
  const history = useHistory();
  const path = useMemo<IBreadcrumbPathItem[]>(() => {
    if (!selectedFolder || !foldersList) return [];
    return getBreadcrumbPath(selectedFolder, foldersList) || [];
  }, [foldersList, selectedFolder]);

  const onBreadcrumbClick = (crumb: IBreadcrumbPathItem, level: number, crumbs: IBreadcrumbPathItem[]) => {
    const path = getPathFromCrumb(crumb, level, crumbs);

    if (path) {
      history.push(path);
    }
  };

  return (
    <Breadcrumb as={StyledBreadcrumbContainer}>
      {path.map((item, id, array) => (
        <Breadcrumb.Item
          as={StyledBreadcrumbItem}
          key={id}
          active={id === array.length - 1}
          onClick={() => onBreadcrumbClick(item, id, array)}
        >
          {t(item.name)}
        </Breadcrumb.Item>
      ))}
    </Breadcrumb>
  );
};

export { ResourcesBreadcrumb };

const StyledBreadcrumbContainer = styled.ul`
  display: flex;
  font-weight: 400;
  font-size: 14px;
  padding: 0;

  .breadcrumb {
    padding: 0;
    border-radius: 0;
    background-color: transparent;
  }
`;

const StyledBreadcrumbItem = styled.li`
  color: ${colors.gray.c500};

  a {
    color: inherit;
    text-decoration: none;
  }

  &.active {
    font-weight: 700;
    color: ${otherColors.lxc.blue};

    &:before {
      font-weight: 400;
    }
  }
`;
